/**
 * MOCK/TEMPORARY — products, entitlements and purchases.
 *
 * ============================================================================
 * NO PAYMENTS. `purchaseProduct` charges nothing and simply reports success, at
 * which point the entitlements store flips a boolean. Entitlements are
 * therefore client-side and trivially editable in devtools.
 *
 * That is a known, accepted limitation of the prototype, not an oversight: real
 * gating requires server-authoritative entitlements plus webhook-driven
 * fulfilment. This module is the seam where that lands.
 * ============================================================================
 *
 * Real endpoints: docs/api-contract.md, "Products", "Entitlements", "Payments".
 */
import { USE_MOCK_API } from '../config';
import { COMMERCIAL_ADDON } from '../data/products';
import { CURRENT_PRODUCTS } from '../data/upgradeOffers';
import {
  claimMockBonus,
  getMockBonus,
  listMockBonuses,
  listMockClaimedBonuses,
} from './mockBonuses';
import type {
  BonusClaimResult,
  BonusPack,
  ContentAvailability,
  Entitlement,
  Product,
  ProductId,
} from '../types/domain';
import { ApiError, assertMock, mockDelay } from './mock';
import { http } from './http';
import { customerBonusPack } from '../lib/customerBonusPack';
import { vaultApi } from './vaultApi';

/** Every entitlement off — a brand-new account owns only the front-end product. */
export const EMPTY_ENTITLEMENT: Entitlement = {
  commercial: false,
  studio: false,
  composerPlus: false,
  professional: false,
  unlimited: false,
  dfyVault: false,
  reseller: false,
  publisher: false,
  commercialPurchaseDate: '',
};

/** Maps a purchasable product id to the entitlement flag it unlocks. */
export const PRODUCT_ENTITLEMENT: Record<ProductId, keyof Entitlement> = {
  commercial: 'commercial',
  studio: 'studio',
  composer: 'composerPlus',
  professional: 'professional',
  unlimited: 'unlimited',
  vault: 'dfyVault',
  reseller: 'reseller',
  publisher: 'publisher',
};

export const productsApi = {
  /** MOCK: GET /products — the catalogue is static fixture data. */
  async getProducts(): Promise<Product[]> {
    if (!USE_MOCK_API) {
      return http.get<Product[]>('/products');
    }
    assertMock('productsApi.getProducts');
    return mockDelay([...CURRENT_PRODUCTS]);
  },

  /**
   * MOCK: GET /entitlements
   *
   * Always returns the empty set. The real endpoint derives this from purchase
   * records, and it is the ONLY authority the UI should trust for gating.
   */
  async getEntitlements(): Promise<Entitlement> {
    if (!USE_MOCK_API) {
      return http.get<Entitlement>('/entitlements');
    }
    assertMock('productsApi.getEntitlements');
    return mockDelay({ ...EMPTY_ENTITLEMENT });
  },

  /** Lightweight user-specific catalog fingerprints for sidebar update indicators. */
  async getContentAvailability(): Promise<ContentAvailability> {
    if (!USE_MOCK_API) {
      return http.get<ContentAvailability>('/products/content-availability');
    }
    assertMock('productsApi.getContentAvailability');
    const [bonuses, vaultPacks] = await Promise.all([this.getBonuses(), vaultApi.getPacks()]);
    return {
      bonusIds: bonuses.map((bonus) => bonus.id),
      vaultPackIds: vaultPacks.map((pack) => pack.id),
    };
  },

  /**
   * MOCK: POST /payments/checkout then wait for fulfilment
   *
   * Takes no money and validates nothing beyond the product existing. Returns
   * the purchased product so the caller can name it in a toast.
   */
  async purchaseProduct(productId: ProductId): Promise<Product> {
    if (!USE_MOCK_API) {
      return http.post<Product>('/payments/checkout', { kind: 'product', productId });
    }
    assertMock('productsApi.purchaseProduct');
    const checkoutIdByEntitlement: Record<ProductId, Product['id']> = {
      commercial: 'commercial-certification',
      studio: 'studio-card',
      composer: 'music-dna-composer',
      professional: 'professional',
      unlimited: 'unlimited-creation-publishing',
      vault: 'dfy-producer-vault',
      reseller: 'reseller',
      publisher: 'publisher-monetization',
    };
    const product = CURRENT_PRODUCTS.find(
      (candidate) => candidate.id === checkoutIdByEntitlement[productId]
    );
    if (!product) {
      throw new ApiError(404, `Unknown product "${productId}"`);
    }
    return mockDelay(product);
  },

  /** MOCK: POST /payments/checkout for the Commercial License & Creator Toolkits order bump. */
  async purchaseCommercial(): Promise<Product | undefined> {
    if (!USE_MOCK_API) {
      return http.post<Product>('/payments/checkout', { kind: 'product', productId: 'commercial' });
    }
    assertMock('productsApi.purchaseCommercial');
    return mockDelay(COMMERCIAL_ADDON);
  },

  /** MOCK: GET /products/bonuses */
  async getBonuses(): Promise<BonusPack[]> {
    if (!USE_MOCK_API) {
      return http.get<BonusPack[]>('/products/bonuses');
    }
    assertMock('productsApi.getBonuses');
    return mockDelay(
      listMockBonuses({ claimedBonusIds: listMockClaimedBonuses() }).map(customerBonusPack)
    );
  },

  /** MOCK: GET /products/bonuses/:id */
  async getBonusPack(bonusId: string): Promise<BonusPack> {
    if (!USE_MOCK_API) {
      return http.get<BonusPack>(`/products/bonuses/${bonusId}`);
    }
    assertMock('productsApi.getBonusPack');
    if (!listMockClaimedBonuses().includes(bonusId)) {
      throw new ApiError(400, 'Claim this bonus before opening its pack.', 'bad_request');
    }
    const bonus = getMockBonus(bonusId, {
      includeDeleted: true,
      includeUnpublished: true,
      claimedBonusIds: listMockClaimedBonuses(),
    });
    if (!bonus) {
      throw new ApiError(404, 'Bonus pack not found.', 'not_found');
    }
    return mockDelay(customerBonusPack(bonus));
  },

  /** MOCK: GET /products/claimed-bonuses */
  async getClaimedBonuses(): Promise<string[]> {
    if (!USE_MOCK_API) {
      return http.get<string[]>('/products/claimed-bonuses');
    }
    assertMock('productsApi.getClaimedBonuses');
    return mockDelay(listMockClaimedBonuses());
  },

  /** MOCK: POST /products/:id/claim-bonus */
  async claimBonus(bonusId: string): Promise<BonusClaimResult> {
    if (!USE_MOCK_API) {
      return http.post<BonusClaimResult>(`/products/${bonusId}/claim-bonus`);
    }
    assertMock('productsApi.claimBonus');
    const bonus = getMockBonus(bonusId, {
      includeDeleted: true,
      includeUnpublished: true,
      claimedBonusIds: listMockClaimedBonuses(),
    });
    if (!bonus) {
      throw new ApiError(400, 'Choose a valid bonus.', 'bad_request');
    }
    if (bonus.deletedAt) {
      throw new ApiError(400, 'This bonus pack is no longer available.', 'bad_request');
    }
    const alreadyClaimed = listMockClaimedBonuses().includes(bonusId);
    claimMockBonus(bonusId);
    return mockDelay({
      bonusId: bonus.id,
      bonusName: bonus.name,
      alreadyClaimed,
      ...(bonus.creditGrant
        ? {
            creditGrant: {
              amount: bonus.creditGrant.amount,
              kind: bonus.creditGrant.kind,
            },
          }
        : {}),
    });
  },
};
